import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import TemperatureDisplay from "@ui/components/temperature/TemperatureDisplay";
import { getNodeTemperatureStatus } from "@ui/components/temperature";
import { FreeRoamingBadge } from "@ui/components/badges/FreeRoamingBadge";
import { TemperatureStatusChip } from "@ui/components/grid/DeviceDetailsRow";
import { hasEnvironmentReadings } from "@ui/utils/environment-readings";
import { SensorType, isSensorHidden } from "@ui/utils/sensors";
import { limitReasons, worstRowStatus } from "@ui/utils/sensor-limit-status";
import { connectedNodeTileColor, toConnectedNodeDisplay } from "../connectedNodeDisplay";
import { SensorReading } from "./SensorReading";
export const nodeGridRowDisplay = (row, t) => toConnectedNodeDisplay({
    nodeTypeCode: row.nodeType,
    nodeUid: row.nodeId,
    nodeName: row.nodeName,
    nodeTemprature: row.nodeTemperature,
    lowTemperature: row.lowTemperature,
    highTemperature: row.highTemperature,
    externalTemperature: row.externalTemperature,
    disabledSensors: row.disabledSensors,
    temperatureName: row.temperatureName,
}, t);
/** The main temperature tile. */
export const NodeTemperatureTile = ({ row }) => {
    const { t } = useTranslation("common");
    const display = nodeGridRowDisplay(row, t);
    return (React.createElement(TemperatureDisplay, Object.assign({ width: 91, temperature: display.nodeTemperature, color: connectedNodeTileColor({
            nodeTemperature: display.nodeTemperature,
            lowTemperature: display.lowTemperature,
            highTemperature: display.highTemperature,
            isActive: row.isActive,
            externalAsMain: display.externalAsMain,
            sensorStatuses: row.sensorStatuses,
        }), external: display.externalAsMain, infoTooltip: display.tileInfoTooltip }, (row.nodeType === 1 ? { leftIndicator: 1, rightIndicator: 2 } : {}))));
};
/** The external temperature tile; none when it is off or already the main tile. */
export const NodeExternalTemperatureTile = ({ row }) => {
    const { t } = useTranslation("common");
    const display = nodeGridRowDisplay(row, t);
    if (!hasEnvironmentReadings({ nodeTypeCode: row.nodeType }) ||
        display.externalAsMain ||
        isSensorHidden(row.disabledSensors, SensorType.ExternalTemperature))
        return null;
    return (React.createElement(SensorReading, { value: row.externalTemperature, unit: "\u00B0C", decimals: 1, tone: "external", inactive: !row.isActive, sensorType: SensorType.ExternalTemperature, sensorLabel: display.externalTooltip, statuses: row.sensorStatuses }));
};
/** The humidity tile; none when it is off. */
export const NodeHumidityTile = ({ row }) => {
    const { t } = useTranslation("common");
    if (!hasEnvironmentReadings({ nodeTypeCode: row.nodeType }) ||
        isSensorHidden(row.disabledSensors, SensorType.Humidity))
        return null;
    return (React.createElement(SensorReading, { value: row.humidity, unit: "%", tone: "humidity", inactive: !row.isActive, sensorType: SensorType.Humidity, sensorLabel: t("labels.humidity"), statuses: row.sensorStatuses }));
};
/** The status chip (worst of the temperature and the sensors), with the free roaming icon in front of it. */
export const NodeStatusCell = ({ row }) => {
    const { t } = useTranslation("common");
    const display = nodeGridRowDisplay(row, t);
    const status = worstRowStatus(getNodeTemperatureStatus({
        nodeTemperature: display.nodeTemperature,
        lowTemperature: display.lowTemperature,
        highTemperature: display.highTemperature,
        isActive: row.isActive,
    }), row.sensorStatuses);
    return (React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5 } },
        row.freeRoamingNode && React.createElement(FreeRoamingBadge, null),
        React.createElement(TemperatureStatusChip, { status: status, reason: limitReasons(row.sensorStatuses, t) })));
};
